import React, { useState } from 'react';
import {
  SearchOverlayWrapper,
  GridContent,
  SubHeading,
  Heading,
  SearchOverlayWrapperStyle,
} from './styles';
import { Grid, GridRow, GridItem } from '../Grid';
import { PopularSearch } from '../PopularSearch';
import FadeInSection from '../FadeInSection';
import { CardCarousel } from '../CardCarousel';
import { TeaserLarge } from 'components/TeaserLarge';
import { TeaserSmall } from 'components/TeaserSmall';
import type { ISearchOverlayTitles, IPopularSearchData } from './types';
import type { SearchSubmitHandler } from '../../types/searchSubmitHandler';
import { useAlgoliaExtensionCardData } from '../../hooks/useAlgoliaExtensionCardData';
import { useGlobalContext } from 'components/Context/hooks/useGlobalContext';
import { WindowScrollService } from 'service/windowScroll/windowsScrollService';

// Uses Algolia Instant Search React widgets
// See: https://www.algolia.com/doc/api-reference/widgets/react/

/**
 * Static Carousel configurations
 */
const carouselSettings = {
  slidesToShow: 3.5,
  slidesToScroll: 1,
  adaptiveHeight: false,
  infinite: true,
  arrows: true,
  dots: true,
  lazyLoad: 'ondemand',
  centerMode: false,
  responsive: [
    {
      breakpoint: 480,
      settings: {
        slidesToShow: 1,
        arrows: false,
      },
    },
  ],
};

/**
 * Reference to be passed down to the searchbox component.
 * Used for the *SearchOverlay* to be able to get/control the search input element on the UI
 */
const searchBoxRef = React.createRef<HTMLInputElement>();

export const SearchOverlay = ({
  searchData,
  popularSearchData,
  featuredCarousel,
  searchSubmitHandler,
  setOpenSearch,
  children,
  trackingEvents,
}: {
  searchData?: ISearchOverlayTitles;
  popularSearchData?: IPopularSearchData;
  featuredCarousel?: any;
  searchSubmitHandler: SearchSubmitHandler;
  setOpenSearch: (open: boolean) => void;
  children: any;
  trackingEvents?: {
    clickPopularSearch?: (item: any, index: number) => void;
    clickSuggestion?: (item: any, index: number, searchBoxRef: any, hitsNumber: number) => void;
    clickSearchAllSuggestions?: (searchBoxRef: any, hitsNumber: number) => void;
  };
}) => {
  const { pathTranslations } = useGlobalContext();
  const [hitsNumber, setHitsNumber] = useState<number>(-1);
  const [show, setShow] = useState<boolean>(false);

  const handleSubmit = (value: string) => {
    setOpenSearch(false);
    searchSubmitHandler(value);
  };

  const extensionData = useAlgoliaExtensionCardData(
    featuredCarousel?.carouselData,
    featuredCarousel?.carouselData?.length,
    pathTranslations,
  );
  const finalCardData = extensionData?.slice(0, 30);

  return (
    <SearchOverlayWrapper
      data-testid="searchOverlay"
      id="search-overlay"
      className={SearchOverlayWrapperStyle}
    >
      <Grid>
        <GridRow columns={16}>
          <GridItem colSpan={12} colStart={3} className="animate-slideDown">
            <GridContent data-test="card-carousel-grid-content">
              <SubHeading tag="p" type="lead" data-test="search-overlay-super">
                {searchData?.subHeading}
              </SubHeading>
              <Heading tag="h2" type="md" data-test="search-overlay-title">
                {searchData?.heading}
              </Heading>
            </GridContent>
            {searchData?.searchClient &&
              React.cloneElement(children, {
                searchData,
                pathTranslations,
                handleSubmit,
                searchSubmitHandler,
                setHitsNumber,
                hitsNumber,
                setShow,
                show,
                ref: searchBoxRef,
                onClick: () => {
                  setOpenSearch(false);
                  WindowScrollService.clearAllBodyElementLocks();
                },
              })}
          </GridItem>
        </GridRow>
      </Grid>
      {searchData?.teasersLarge?.length > 0 && <TeaserLarge teasers={searchData?.teasersLarge} />}
      {searchData?.teasersSmall?.length > 0 && (
        <TeaserSmall teasersSmall={searchData?.teasersSmall} />
      )}
      <FadeInSection delay="0.1ms">
        <PopularSearch
          componentData={popularSearchData?.componentData}
          hits={popularSearchData?.hits}
          onItemClick={() => {
            setOpenSearch(false);
            WindowScrollService.clearAllBodyElementLocks();
          }}
          trackingEvents={{ clickPopularSearch: trackingEvents?.clickPopularSearch }}
        />
        {featuredCarousel?.carouselData && finalCardData ? (
          <CardCarousel
            carouselData={featuredCarousel.carouselData}
            cardData={finalCardData}
            copyDictionary={featuredCarousel.copyDictionary}
            showInfo
            cardType="recipes"
            settings={carouselSettings}
          />
        ) : (
          ''
        )}
      </FadeInSection>
    </SearchOverlayWrapper>
  );
};
